import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import api from '../services/api';
import LoginCard from '../components/LoginCard';
import './Login.css';

const Login = () => {
  const { showError } = useNotification();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!email || !password) {
      setError('Please enter both email and password');
      setIsLoading(false);
      return;
    }

    try {
      const result = await login(email, password);

      if (result && result.success === true) {
        // Only navigate on success
        navigate('/');
        return;
      } 
      
      // Handle error
      // Backend returns: { error: "UNAUTHORIZED", message: "Invalid credentials", ... }
      let errorMessage = 'Invalid credentials. Please check your username and password.';
      
      if (result) {
        // Priority 1: Check errorData.message (from backend response)
        if (result.errorData && result.errorData.message) {
          errorMessage = result.errorData.message;
        }
        // Priority 2: Check result.error (from AuthContext)
        else if (result.error) {
          errorMessage = result.error;
        }
        // Priority 3: Check errorData.error
        else if (result.errorData && result.errorData.error === 'UNAUTHORIZED') {
          errorMessage = 'Invalid credentials. Please check your username and password.';
        }
      }
      
      // Set error state
      setError(errorMessage);
      setIsLoading(false);
      
    } catch (err) {
      // Catch any unexpected errors
      console.error('=== UNEXPECTED ERROR ===');
      console.error('Error:', err);
      const errorMessage = 'Invalid credentials. Please check your username and password.';
      setError(errorMessage);
      showError(errorMessage);
      setIsLoading(false);
    }
  };

  const togglePassword = useCallback(() => {
    setShowPassword((v) => !v);
  }, []);

  const handleMicrosoftSignIn = useCallback(() => {
    setError('');
    setIsMicrosoftLoading(true);
    try {
      api.startMicrosoftLogin();
    } catch (err) {
      console.error('Microsoft sign-in start failed:', err);
      setError('Unable to start Microsoft sign-in. Please try again.');
      setIsMicrosoftLoading(false);
    }
  }, []);

  return (
    <div className="login-container">
      <div className="login-wrapper">
        {/* Left Side - Company Info */}
        <div className="login-info-section">
          <div className="login-info-content">
            <div className="login-logo-large">
              <img src="/ebotics.png" alt="Ebotics Logo" />
            </div>
            <h1 className="welcome-title">Welcome to eBotics Solutions</h1>
            <p className="welcome-subtitle">
              Automating Healthcare Revenue-Cycle Management for hospitals, clinics, and provider groups
            </p>
            <div className="features-list">
              <div className="feature-item">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 10L9.16667 11.6667L12.5 8.33333M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Medical Billing & Charge Posting</span>
              </div>
              <div className="feature-item">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 10L9.16667 11.6667L12.5 8.33333M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Eligibility & Benefit Verification</span>
              </div>
              <div className="feature-item">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 10L9.16667 11.6667L12.5 8.33333M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Claims & Denial Management</span>
              </div>
              <div className="feature-item">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 10L9.16667 11.6667L12.5 8.33333M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Provider Credentialing & Enrollment</span>
              </div>
              <div className="feature-item">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 10L9.16667 11.6667L12.5 8.33333M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Back-Office Automation & Virtual Assistant Services</span>
              </div>
            </div>
            <div className="welcome-footer">
              <p>Automate the repetitive. Boost accuracy, save time, and maximize revenue.</p>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="login-form-section">
          <LoginCard
            email={email}
            onEmailChange={setEmail}
            password={password}
            onPasswordChange={setPassword}
            showPassword={showPassword}
            onToggleShowPassword={togglePassword}
            error={error}
            isLoading={isLoading || isMicrosoftLoading}
            isMicrosoftLoading={isMicrosoftLoading}
            onSubmit={handleSubmit}
            onMicrosoftSignIn={handleMicrosoftSignIn}
          />
        </div>
      </div>
    </div>
  );
};

export default Login;

